/* eslint-disable @typescript-eslint/ban-types */
import React, { forwardRef, MutableRefObject, useMemo } from 'react';
import BaseTable, { BaseTableProps, AutoResizer, Column, ColumnShape, SortOrder } from 'react-base-table';
import 'react-base-table/styles.css';
import cc from 'classcat';
import { WMRefForwardingComponent } from '@walkme/wm-ui';

import { defaultMinWidth, components, getScrollbarSize, getInlineStyle, Empty, LoadMore, defaultRowHeight } from './utils';
import styles from './WMTable.module.scss';
import { AppTableFilter } from '../model';

export type WMTableSortOrder = SortOrder;

export enum FilterTypes {
    FILTER,
    DATASOURCE
}

export type WMTableColumnFilterMetaData<T = $TSFixMe> = {
    active: boolean;
    hideSearch: boolean;
    type?: FilterTypes;
    dataSources?: {
        values: Array<{ value: string; label: string }>;
        defaultValue: string;
    };
    execFilter?: ((item: T, checkedList: string[]) => boolean | undefined) | (() => true);
    extractFilterValues?: (item: T) => string[] | string | undefined;
};

export type WMTableColumnShape<T = $TSFixMe> = Omit<ColumnShape<T>, 'width' | 'sortable'> & {
    dataKey: string;
    filterMetaData?: WMTableColumnFilterMetaData;
    cellRenderer?: ColumnShape<T>['cellRenderer'];
    renderFilterItem?: (filter: AppTableFilter) => ReturnType<React.FC<React.PropsWithChildren<unknown>>>;
    skeleton?: {
        width: number;
        height: number;
    };
};

export type WMTableProps = {
    loading?: boolean;
    loadingMore?: boolean;
    /**
     * Using `maxWidth` will override `defaultMinWidth` (1280px).
     */
    maxWidth?: number;
    emptyRendererHeight?: number;
    paddingTableBodyClassName?: string;
    disableCellPaddings?: boolean;
    noBorder?: boolean;
} & Omit<BaseTableProps, 'children' | 'components'>;

export type WMTableRef = BaseTable<unknown>;

type WMTableRefType = ((instance: WMTableRef | null) => void) | MutableRefObject<WMTableRef | null> | null;

type WMTableComponent = WMRefForwardingComponent<WMTableProps, WMTableRef>;

/**
 * `WMTable` internally uses its wrapper's height as a max-height, therefore, **its wrapper must have an explicit height**.
 *
 * For example:
 *
 * ```
 * <div style={{ height: 400 }}><WMTable /></div>
 * ```
 * OR - maxHeight can be passed explicitly as a prop. For allowing height to be flexible downwards.
 *
 * For more info: https://autodesk.github.io/react-base-table/api/basetable.
 */
const WMTable: WMTableComponent = forwardRef(
    (
        {
            className,
            style,
            data,
            columns,
            loading = false,
            loadingMore = false,
            footerRenderer = null,
            emptyRenderer = () => <Empty loading={loading} />,
            emptyRendererHeight,
            maxWidth,
            minWidth = defaultMinWidth,
            headerHeight = 56,
            rowHeight = defaultRowHeight,
            maxHeight,
            paddingTableBodyClassName,
            ...otherProps
        }: WMTableProps,
        ref: WMTableRefType
    ) => {
        const dataSource = useMemo(() => data.map((row: {}, index: number) => ({ ...row, rowKey: index })), [data]);

        const footerHeight = footerRenderer ? 33 : 0;
        const fullHeight = data?.length * rowHeight + headerHeight + footerHeight;

        const moreProps =
            process.env.NODE_ENV === 'test'
                ? {
                      width: 500,
                      height: 500
                  }
                : null;

        return (
            <AutoResizer
                className={cc([
                    styles.wmTable,
                    { [styles.withMaxHeight]: maxHeight, [styles.disableCellPaddings]: !!otherProps.disableCellPaddings }
                ])}
                height={maxHeight ?? undefined}
                {...moreProps}
            >
                {({ width, height }) => {
                    const calculatedWidth = maxWidth ? Math.min(maxWidth, Math.max(minWidth, width)) : Math.max(minWidth, width);
                    const emptyRendererPadding = data?.length === 0 ? emptyRendererHeight || rowHeight : 0;

                    return (
                        <div
                            style={{
                                paddingBottom: emptyRendererPadding
                            }}
                        >
                            <BaseTable
                                ref={ref}
                                {...otherProps}
                                className={cc([
                                    styles.table,
                                    className,
                                    { [styles.noBorder]: otherProps.noBorder },
                                    paddingTableBodyClassName ? { [paddingTableBodyClassName]: height < fullHeight } : {}
                                ])}
                                style={getInlineStyle(style, calculatedWidth)}
                                data={dataSource}
                                rowKey="rowKey"
                                width={calculatedWidth}
                                maxHeight={height}
                                headerHeight={headerHeight}
                                rowHeight={rowHeight}
                                footerHeight={footerHeight}
                                components={components}
                                getScrollbarSize={getScrollbarSize}
                                footerRenderer={footerRenderer}
                                emptyRenderer={emptyRenderer}
                                overlayRenderer={loadingMore ? <LoadMore height={50 + footerHeight} /> : null}
                            >
                                {columns.map((col: WMTableColumnShape, idx: number) => (
                                    <Column
                                        {...col}
                                        key={col.key ?? idx}
                                        className={cc([styles.cell, col.className])}
                                        headerClassName={cc([styles.head, col.headerClassName])}
                                        width={col.width ?? 100}
                                        minWidth={col.minWidth}
                                        flexGrow={col.flexGrow ?? 1}
                                    />
                                ))}
                            </BaseTable>
                        </div>
                    );
                }}
            </AutoResizer>
        );
    }
);

WMTable.displayName = 'WMTable';

export default WMTable;
